import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { getOrdinal } from '../../../functions';
import {
  ILeaderboard,
  ILeaderboardEntry,
} from '../../../Models/LeaderboardModels';
import Avatar, { BotIcon, CrownIcon } from '../Avatar/Avatar';
import { FeltTexture } from '../FeltMat/feltParts';
import SegmentedControl from '../StartScreen/SegmentedControl';
import StartScreen from '../StartScreen/StartScreen';
import { CARD_CLASS_NAME } from '../StartScreen/styles';

interface ILeaderboardProps {
  leaderboard: ILeaderboard;
  refreshLeaderboard: () => Promise<void>;
  onBackButtonClick: () => void;
}

type Filter = 'everyone' | 'players' | 'bots';

interface IRankedEntry {
  entry: ILeaderboardEntry;
  rank: number;
  isTied: boolean;
}

const FILTERS: { value: Filter; label: string }[] = [
  { value: 'everyone', label: 'Everyone' },
  { value: 'players', label: 'Players' },
  { value: 'bots', label: 'Bots' },
];

const EMPTY_TEXT: Record<Filter, string> = {
  everyone: 'No results yet',
  players: 'No players yet',
  bots: 'No bots yet',
};

const PODIUM_ORDER = ['order-2', 'order-1', 'order-3'];

const PODIUM_STEPS = [
  {
    avatar: 'size-[60px] text-[24px]',
    step: 'h-[64px] bg-accent-fill text-accent-on',
  },
  {
    avatar: 'size-[48px] text-[20px]',
    step: 'h-[46px] bg-straw text-straw-ink',
  },
  {
    avatar: 'size-[48px] text-[20px]',
    step: 'h-[32px] bg-surface text-ink',
  },
];

function podiumStep(rank: number) {
  return PODIUM_STEPS[Math.min(rank, PODIUM_STEPS.length) - 1];
}

function plural(count: number, word: string, pluralWord = `${word}s`) {
  return `${count} ${count === 1 ? word : pluralWord}`;
}

function recordText({ wins, losses, forfeits }: ILeaderboardEntry) {
  return [
    plural(wins, 'win'),
    plural(losses, 'loss', 'losses'),
    forfeits > 0 && plural(forfeits, 'forfeit'),
  ]
    .filter(Boolean)
    .join(' · ');
}

function formatPoints(points: number) {
  return points < 0 ? `−${-points}` : `${points}`;
}

function pointsUnit(points: number) {
  return Math.abs(points) === 1 ? 'pt' : 'pts';
}

function nameColorIndex(name: string) {
  return Array.from(name).reduce((sum, char) => sum + char.charCodeAt(0), 0);
}

function rankEntries(entries: ILeaderboardEntry[]): IRankedEntry[] {
  return entries.map((entry) => {
    const tiedEntries = entries.filter(
      (other) => other.points === entry.points
    );
    return {
      entry,
      rank: entries.indexOf(tiedEntries[0]) + 1,
      isTied: tiedEntries.length > 1,
    };
  });
}

export function Ordinal({ rank }: { rank: number }) {
  return (
    <span className="tabular-nums">
      {rank}
      <span className="text-[0.7em]">{getOrdinal(rank)}</span>
    </span>
  );
}

function placeText(rank: number, isTied: boolean) {
  return `${rank}${getOrdinal(rank)} place${isTied ? ', tied' : ''}, `;
}

function Podium({ top }: { top: IRankedEntry[] }) {
  return (
    <ol
      aria-label="Top three"
      className="relative isolate flex items-end justify-center gap-[8px] overflow-hidden rounded-[24px] bg-felt px-[12px] pt-[18px] text-felt-ink shadow-mat"
    >
      <FeltTexture />
      {top.map(({ entry, rank, isTied }, i) => (
        <li
          key={`${entry.name}-${i}`}
          className={classNames(
            'flex max-w-[120px] min-w-0 flex-1 flex-col items-center',
            PODIUM_ORDER[i]
          )}
        >
          <span className="sr-only">{placeText(rank, isTied)}</span>
          {rank === 1 && (
            <CrownIcon className="mb-[2px] text-accent-fill drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)]" />
          )}
          <span className="relative">
            <Avatar
              name={entry.name}
              colorIndex={nameColorIndex(entry.name)}
              className={classNames(
                'shadow-felt-chip ring-3 ring-white/90',
                podiumStep(rank).avatar
              )}
            />
            {entry.isBot && (
              <span className="absolute -right-[4px] -bottom-[4px] flex size-[22px] items-center justify-center rounded-full bg-surface text-ink shadow-felt-chip">
                <BotIcon size={14} />
                <span className="sr-only">Bot</span>
              </span>
            )}
          </span>
          <span className="mt-[8px] max-w-full truncate text-[14px] font-bold">
            {entry.name}
          </span>
          <span className="mt-[4px] rounded-full bg-black/24 px-[9px] py-[3px] text-[12px] font-semibold tabular-nums">
            {formatPoints(entry.points)} {pointsUnit(entry.points)}
          </span>
          <span
            aria-hidden="true"
            className={classNames(
              'mt-[10px] flex w-full items-baseline justify-center gap-[4px] rounded-t-[12px] pt-[6px] text-[20px] font-bold tabular-nums shadow-felt-chip',
              podiumStep(rank).step
            )}
          >
            <Ordinal rank={rank} />
            {isTied && (
              <span className="text-[12px] font-semibold opacity-70">tied</span>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

interface ILeaderboardRowProps extends IRankedEntry {
  joinsAbove: boolean;
  joinsBelow: boolean;
}

const TIE_LINE_CLASS_NAME =
  'absolute left-1/2 w-[2px] -translate-x-1/2 rounded-full bg-ink/15';

function LeaderboardRow({
  entry,
  rank,
  isTied,
  joinsAbove,
  joinsBelow,
}: ILeaderboardRowProps) {
  return (
    <li className="flex h-[60px] items-center gap-[12px] px-[14px]">
      <span className="sr-only">{placeText(rank, isTied)}</span>
      <span
        aria-hidden="true"
        className="relative flex w-[32px] shrink-0 flex-col items-center justify-center self-stretch text-ink-muted"
      >
        {joinsAbove ? (
          <>
            <span
              className={classNames(TIE_LINE_CLASS_NAME, 'top-0 bottom-1/2')}
            />
            <span className="relative size-[6px] rounded-full bg-ink/30" />
          </>
        ) : (
          <>
            <span className="text-[13px] font-bold">
              <Ordinal rank={rank} />
            </span>
            {isTied && (
              <span className="-mt-[2px] text-[10px] font-semibold">tied</span>
            )}
          </>
        )}
        {joinsBelow && (
          <span
            className={classNames(
              TIE_LINE_CLASS_NAME,
              'bottom-0',
              joinsAbove ? 'top-1/2' : 'top-[calc(50%+18px)]'
            )}
          />
        )}
      </span>
      <Avatar
        name={entry.name}
        colorIndex={nameColorIndex(entry.name)}
        className="size-[32px] text-[14px]"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-[2px]">
        <span className="flex min-w-0 items-center gap-[6px]">
          <span className="truncate text-[15px] font-semibold">
            {entry.name}
          </span>
          {entry.isBot && (
            <span className="flex shrink-0 items-center gap-[3px] rounded-full bg-control py-[2px] pr-[7px] pl-[5px] text-[11px] font-bold text-ink-muted">
              <BotIcon size={12} />
              Bot
            </span>
          )}
        </span>
        <span className="truncate text-[12px] font-medium text-ink-muted">
          {recordText(entry)}
        </span>
      </span>
      <span className="shrink-0 text-[16px] font-bold tabular-nums">
        {formatPoints(entry.points)}
        <span className="ml-[3px] text-[11px] font-semibold text-ink-muted">
          {pointsUnit(entry.points)}
        </span>
      </span>
    </li>
  );
}

export function Leaderboard({
  leaderboard,
  refreshLeaderboard,
  onBackButtonClick,
}: ILeaderboardProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('everyone');

  useEffect(() => {
    refreshLeaderboard().finally(() => setIsLoading(false));
  }, [refreshLeaderboard]);

  const { entries } = leaderboard;
  const ranked = rankEntries(
    entries.filter(
      (entry) => filter === 'everyone' || entry.isBot === (filter === 'bots')
    )
  );

  const rest = ranked.slice(3);

  return (
    <StartScreen
      title="Leaderboard"
      backLabel="Back to games"
      onBack={onBackButtonClick}
    >
      {entries.length === 0 ? (
        <span className="pt-[72px] text-center text-[14px] font-medium text-ink-muted">
          {isLoading ? 'Loading…' : EMPTY_TEXT.everyone}
        </span>
      ) : (
        <>
          <SegmentedControl
            label="Show"
            options={FILTERS}
            value={filter}
            onChange={setFilter}
          />
          {ranked.length === 0 ? (
            <span className="pt-[48px] text-center text-[14px] font-medium text-ink-muted">
              {EMPTY_TEXT[filter]}
            </span>
          ) : (
            <Podium top={ranked.slice(0, 3)} />
          )}
          {rest.length > 0 && (
            <ol
              aria-label="Everyone else"
              className={classNames(
                CARD_CLASS_NAME,
                '-mt-[4px] divide-y divide-hairline overflow-hidden'
              )}
            >
              {rest.map((row, i) => (
                <LeaderboardRow
                  key={`${row.entry.name}-${i}`}
                  {...row}
                  joinsAbove={rest[i - 1]?.rank === row.rank}
                  joinsBelow={rest[i + 1]?.rank === row.rank}
                />
              ))}
            </ol>
          )}
        </>
      )}
    </StartScreen>
  );
}
